/**
 * Upgrade Confirmation Modal
 *
 * Shows a confirmation dialog before upgrading a subscription.
 * Displays the current plan/bucket, the new plan/bucket, and explains
 * that the prorated difference will be charged immediately.
 *
 * @module components/billing/UpgradeConfirmModal
 */

import { Modal, Button } from '@/components/ui';
import { ArrowRight, CreditCard } from 'lucide-react';
import {
  type PlanId,
  type BillingInterval,
  PLAN_TIERS,
  formatRequestCount,
  formatPrice,
  getBucketForRequestCount,
} from '@hummingbird/pricing';

// =============================================================================
// Types
// =============================================================================

export interface UpgradeConfirmModalProps {
  /** Whether the modal is open */
  open: boolean;
  /** Called when the modal should close */
  onClose: () => void;
  /** Called when the user confirms the upgrade */
  onConfirm: () => void;
  /** Whether the upgrade is in progress */
  isLoading?: boolean;
  /** Current plan ID */
  currentPlanId: PlanId;
  /** Current bucket max requests */
  currentBucketMax: number;
  /** New plan ID */
  newPlanId: PlanId;
  /** New bucket max requests */
  newBucketMax: number;
  /** Billing interval */
  interval: BillingInterval;
}

// =============================================================================
// Component
// =============================================================================

export function UpgradeConfirmModal({
  open,
  onClose,
  onConfirm,
  isLoading = false,
  currentPlanId,
  currentBucketMax,
  newPlanId,
  newBucketMax,
  interval,
}: UpgradeConfirmModalProps) {
  const currentPlan = PLAN_TIERS[currentPlanId];
  const newPlan = PLAN_TIERS[newPlanId];

  const isDowngrade = newBucketMax < currentBucketMax;

  // Format the plan display
  const formatPlanDisplay = (planName: string, bucketMax: number) => {
    if (bucketMax === 0) {
      return planName;
    }
    return `${planName} (${formatRequestCount(bucketMax)} requests)`;
  };

  const currentDisplay = formatPlanDisplay(currentPlan.name, currentBucketMax);
  const newDisplay = formatPlanDisplay(newPlan.name, newBucketMax);

  // Calculate prices
  const currentBucket = currentBucketMax > 0 ? getBucketForRequestCount(currentBucketMax, currentPlanId) : null;
  const newBucket = getBucketForRequestCount(newBucketMax, newPlanId);

  const getCurrentPrice = () => {
    if (!currentBucket) return 0;
    return interval === 'annual' ? currentBucket.annualPriceCents : currentBucket.monthlyPriceCents;
  };

  const getNewPrice = () => {
    if (!newBucket) return 0;
    return interval === 'annual' ? newBucket.annualPriceCents : newBucket.monthlyPriceCents;
  };

  const currentPrice = getCurrentPrice();
  const newPrice = getNewPrice();
  const priceDifference = newPrice - currentPrice;

  const title = isDowngrade ? 'Confirm Downgrade' : 'Confirm Upgrade';
  const description = isDowngrade
    ? 'Review your plan change before confirming.'
    : 'Review your upgrade before confirming.';

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={description}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            disabled={isLoading}
            loading={isLoading}
            leftIcon={<CreditCard className="h-4 w-4" />}
          >
            {isLoading ? 'Processing...' : isDowngrade ? 'Confirm Downgrade' : 'Complete Upgrade'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {/* Plan Change Visualization */}
        <div className="flex items-center justify-between gap-4 p-4 bg-gray-50 rounded-lg">
          {/* Current Plan */}
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              Current
            </p>
            <p className="font-semibold text-gray-900">{currentDisplay}</p>
            {currentPrice > 0 && (
              <p className="text-sm text-gray-600 mt-0.5">
                {formatPrice(currentPrice)}/{interval === 'annual' ? 'year' : 'month'}
              </p>
            )}
          </div>

          {/* Arrow */}
          <div className="flex-shrink-0">
            <ArrowRight className="h-5 w-5 text-gray-400" />
          </div>

          {/* New Plan */}
          <div className="flex-1 text-center">
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
              New
            </p>
            <p className="font-semibold text-brand-600">{newDisplay}</p>
            {newPrice > 0 && (
              <p className="text-sm text-brand-600 mt-0.5">
                {formatPrice(newPrice)}/{interval === 'annual' ? 'year' : 'month'}
              </p>
            )}
          </div>
        </div>

        {/* Billing Information */}
        <div className={`p-4 border rounded-lg ${isDowngrade ? 'bg-amber-50 border-amber-100' : 'bg-blue-50 border-blue-100'}`}>
          <p className={`text-sm ${isDowngrade ? 'text-amber-800' : 'text-blue-800'}`}>
            {isDowngrade ? (
              <>
                <strong>Downgrade Note:</strong> Your request limit will be
                reduced immediately. The lower price will take effect at your
                next billing cycle.
              </>
            ) : (
              <>
                <strong>Upgrade Note:</strong> The prorated difference of approximately{' '}
                <strong>{formatPrice(Math.abs(priceDifference))}</strong> will be charged
                immediately for the remaining time in your billing period. Your next
                billing cycle will reflect the new plan amount of{' '}
                <strong>{formatPrice(newPrice)}/{interval === 'annual' ? 'year' : 'month'}</strong>.
              </>
            )}
          </p>
        </div>
      </div>
    </Modal>
  );
}
